import { Card, Box, Typography, Stack, Select, MenuItem } from "@mui/material";
import RxOutcome from "../CLFitting/RxOutcome";
import { LoadingButton } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FormProvider from "../../../../../components/hook-form/FormProvider";
import { Controller, useForm } from "react-hook-form";
import RHFTextField from "../../../../../components/hook-form/RHFTextField";
import { ICLHandover } from "../../../../../@types/clHandover.type";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { CLHandoverService } from "../../../../../api/contact-lenses/cl-handover.service";
import { useParams, useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "../../../../../components/snackbar";
import { IClinicalRecordSchema } from "../../../../../@types/clinicalRecord.type";
import SignatureDialog from "../../../../../components/signature/SignatureDialog";
import { ClinicalRecords } from "../../../../../api/clinicalRecords.service";
import FinalSummary from "../../Refraction/FinalSummary";

type Props = {
  clinicalRecordId: number;
  tab: (type: string) => void;
  clHandover: ICLHandover;
  showCLForm: boolean;
  getCLHandoverRecords: (clHandoverId: number, clinicalId: number) => void;
  clinicalRecord?: IClinicalRecordSchema | any;
  onUpdate?: () => void;
};

const CLHandover = ({
  clinicalRecordId,
  tab,
  clHandover,
  showCLForm,
  getCLHandoverRecords,
  clinicalRecord,
  onUpdate,
  finalData,
}: Props & { finalData?: any }) => {
  const [visitType, setVisitType] = useState(
    clHandover?.visitType ? clHandover?.visitType : ""
  );
  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [pendingData, setPendingData] = useState<ICLHandover | null>(null);
  const { patientId } = useParams();
  const navigate = useNavigate();

  const hasClinicalData = !!(
    clinicalRecord?.clHistoryAndSymptomsId ||
    clinicalRecord?.anteriorEyeId ||
    clinicalRecord?.contactLensFittingId ||
    clinicalRecord?.contactLensAfterCareId
  );

  const performHandover = async (data: ICLHandover) => {
    const handoverData = { ...data, visitType, clinicalRecordId };
    const formattedData = {
      ...handoverData,
      recallDate: dayjs(data.recallDate).isValid()
        ? dayjs(data.recallDate).format("YYYY-MM-DD")
        : new Date(),
      systemRecallDate: dayjs(data.systemRecallDate).isValid()
        ? dayjs(data.systemRecallDate).format("YYYY-MM-DD")
        : new Date(),
    };
    try {
      const clHandoverService = new CLHandoverService();
      const response = await clHandoverService.createCLHandover(
        patientId!,
        formattedData
      );
      enqueueSnackbar("CL Handover Created Successfully!", {
        variant: "success",
      });
      getCLHandoverRecords(response.data?.id, clinicalRecordId);
      
    } catch (error) {
      enqueueSnackbar("Internal Server Error", {
        variant: "error",
      });
    }
  };

  const handleSignOff = async (signatureData: string) => {
    if (!clinicalRecordId) return;
    setIsSigning(true);
    try {
      const service = new ClinicalRecords();
      await service.signOff(clinicalRecordId, signatureData);
      enqueueSnackbar("Clinical record signed off successfully", {
        variant: "success",
      });
      setIsSignatureDialogOpen(false);

      // PROCEED WITH HANDOVER AFTER SIGN-OFF
      if (pendingData) {
        await performHandover(pendingData);
        setPendingData(null);
      } else {
        onUpdate && onUpdate();
        
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to sign off clinical record", {
        variant: "error",
      });
    } finally {
      setIsSigning(false);
    }
  };

  const methods = useForm<ICLHandover>();

  const { handleSubmit, control, reset } = methods;

  const handleChange = (e: any) => {
    setVisitType(e.target.value);
  };

  const onSubmit = async (data: ICLHandover) => {
    if (hasClinicalData && !clinicalRecord?.signedAt) {
      setPendingData(data);
      setIsSignatureDialogOpen(true);
    } else {
      await performHandover(data);
    }
  };


  useEffect(() => {
    if (showCLForm) {
      reset({
        supplier: "",
        type: "",
        freq: "",
        lens: "",
        solution: "",
        visitType: "",
        recallDate: new Date(),
        systemRecallDate: new Date(),
        patientAdvise: "",
        orderLenses: "",
        handoverNotes: "",
      });
    } else {
      reset({
        supplier: clHandover?.supplier || "",
        type: clHandover?.type || "",
        freq: clHandover?.freq || "",
        lens: clHandover?.lens || "",
        solution: clHandover?.solution || "",
        visitType: clHandover?.visitType || "",
        recallDate: dayjs(clHandover?.recallDate).isValid()
          ? dayjs(clHandover?.recallDate).toDate()
          : new Date(),
        systemRecallDate: dayjs(clHandover?.systemRecallDate).isValid()
          ? dayjs(clHandover?.systemRecallDate).toDate()
          : new Date(),
        patientAdvise: clHandover?.patientAdvise || "",
        orderLenses: clHandover?.orderLenses || "",
        handoverNotes: clHandover?.handoverNotes || "",
      });
    }
  }, [showCLForm, clHandover, reset]);

  useEffect(() => {
    if (clHandover) {
      setVisitType(clHandover?.visitType || "");
    }
  }, [clHandover, setVisitType]);

  return (
    <Card sx={{ p: 3 }}>
      {!hasClinicalData && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="warning.main" gutterBottom>
            No clinical data found.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please complete at least one clinical form before signing off.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <LoadingButton variant="contained" disabled>Sign Off Clinical Record</LoadingButton>
            <LoadingButton variant="outlined" disabled>Save And Next</LoadingButton>
          </Stack>
        </Box>
      )}

      {hasClinicalData && (!clinicalRecord?.signedAt || !["Contact Lenses", "Contact Lens"].includes(clinicalRecord?.type)) && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="info.main" gutterBottom>
            Clinical record must be signed off before handover.
          </Typography>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={() => setIsSignatureDialogOpen(true)}
          >
            Sign Off Clinical Record
          </LoadingButton>
        </Box>
      )}

      {hasClinicalData && clinicalRecord?.signedAt && clinicalRecord?.type === "Contact Lenses" && (
        <Box
          sx={{
            mb: 2,
            mx: 2,
            p: 2,
            border: "1px solid #4caf50",
            borderRadius: 1,
            bgcolor: "#e8f5e9",
          }}
        >
          <Typography variant="subtitle2" color="success.dark">
            Signed Off By: {clinicalRecord.signedByUser?.username || "Practitioner"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Date: {clinicalRecord.signedAt ? new Date(clinicalRecord.signedAt).toLocaleDateString() : "N/A"}
          </Typography>
          <Typography variant="body2" color="success.main" sx={{ fontWeight: "bold" }}>
            Status: Signed
          </Typography>
        </Box>
      )}

      {hasClinicalData && clinicalRecord?.signedAt && clinicalRecord?.type === "Contact Lenses" && (
        <FormProvider
          methods={methods}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box sx={{ px: 2, pt: 1 }}>
            <FinalSummary finalData={finalData} />
          </Box>
          {(showCLForm || !clHandover || Object.keys(clHandover).length === 0) && (
            <Typography sx={{ ml: 3, mt: 2, mb: 1 }} color={"red"}>
              * There's no CL Handover added for this clinical record, please add
              and save!
            </Typography>
          )}
          <Box
            sx={{
              margin: "2% 10%",
            }}>
            {/* <RxOutcome /> */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              ml={1}
              mr={1}
              spacing={2}>
              <Box
                sx={{
                  width: "33.33%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: "60px",
                }}>
                <Typography color={"#637381"} variant="h6" mb={3}></Typography>
                <Typography color={"#637381"} variant="h6" mt={3}>
                  Supplier
                </Typography>
                <Typography color={"#637381"} variant="h6" mt={3}>
                  Type
                </Typography>
                <Typography color={"#637381"} variant="h6" mt={3}>
                  Freq
                </Typography>
                <Typography color={"#637381"} variant="h6" mt={3}>
                  Lens
                </Typography>
                <Typography color={"#637381"} variant="h6" mt={3}>
                  Solution
                </Typography>
                <Typography color={"#637381"} variant="h6" mt={3}>
                  Visit Type
                </Typography>
                <Typography color={"#637381"} variant="h6" mt={5}>
                  Recall Date
                </Typography>
                <Typography color={"#637381"} variant="h6" mt={5}>
                  System Recall Date
                </Typography>
                <Typography color={"#637381"} variant="h6" mt={3}>
                  Patient Advise
                </Typography>
                <Typography color={"#637381"} variant="h6" mt={3}>
                  Order Lenses
                </Typography>
                <Typography color={"#637381"} variant="h6" mt={4} mb={6}>
                  Handover Notes
                </Typography>
              </Box>
              <Box
                sx={{
                  width: "33.33%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                <RHFTextField
                  name="supplier"
                  size="small"
                  sx={{ width: "90%", marginBottom: "10px" }}
                />
                <RHFTextField
                  name="type"
                  size="small"
                  sx={{ width: "90%", marginBottom: "10px" }}
                />
                <RHFTextField
                  name="freq"
                  size="small"
                  sx={{ width: "90%", marginBottom: "10px" }}
                />
                <RHFTextField
                  name="lens"
                  size="small"
                  sx={{ width: "90%", marginBottom: "10px" }}
                />{" "}
                <RHFTextField
                  name="solution"
                  size="small"
                  sx={{ width: "90%", marginBottom: "10px" }}
                />{" "}
                <Select
                  labelId="demo-simple-select-filled-label"
                  id="demo-simple-select-filled"
                  value={visitType}
                  onChange={handleChange}
                  sx={{ width: "90%", marginBottom: "10px" }}>
                  <MenuItem value="New CL RX">New CL RX</MenuItem>
                  <MenuItem value="Update CL RX">Update CL RX</MenuItem>
                  <MenuItem value="Aftercare">Aftercare</MenuItem>
                  <MenuItem value="Replacement">Replacement</MenuItem>
                  <MenuItem value="Trails Evaluation">Trails Evaluation</MenuItem>
                  <MenuItem value="Emergency">Emergency</MenuItem>
                </Select>
                <Controller
                  name="recallDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      onChange={(date) => field.onChange(date)}
                      label="Recall Date"
                      sx={{ width: "90%", marginBottom: "10px" }}
                    />
                  )}
                />
                <Controller
                  name="systemRecallDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      sx={{ width: "90%", marginBottom: "10px" }}
                      onChange={(date) => field.onChange(date)}
                      label="System Recall Date"
                    />
                  )}
                />
                <RHFTextField
                  name="patientAdvise"
                  size="small"
                  sx={{ width: "90%", marginBottom: "10px" }}
                />{" "}
                <RHFTextField
                  name="orderLenses"
                  size="small"
                  sx={{ width: "90%", marginBottom: "10px" }}
                />{" "}
                <RHFTextField
                  name="handoverNotes"
                  size="small"
                  multiline
                  rows={2}
                  sx={{ width: "90%", marginBottom: "10px" }}
                />
              </Box>
            </Stack>
            <Box sx={{ display: "flex", flexDirection: "row-reverse", m: 3 }}>
              <LoadingButton variant="contained" type="submit">
                Save And Next
              </LoadingButton>
            </Box>
          </Box>
        </FormProvider>
      )}
      <SignatureDialog
        open={isSignatureDialogOpen}
        onClose={() => setIsSignatureDialogOpen(false)}
        onConfirm={handleSignOff}
        loading={isSigning}
      />
    </Card>
  );
};

export default CLHandover;
